import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { ReactNode } from "react";
import { useSession } from "@core/session/SessionProvider";
import { tokens } from "@core/theme/tokens";
import { renewSession } from "@portal/access/api";

// SPEC-201 §3: cierre por inactividad según `security.session_idle_minutes`,
// aviso 2 minutos antes, y al expirar el portal "vuelve al acceso
// conservando la ruta para retomar donde estaba".
//
// FINDING: no se encontró en el contrato (api/contracts/) ningún endpoint que
// exponga `security.session_idle_minutes` al portal — StaffSession
// (00-base.yaml:518) no lo incluye y no hay ruta de parámetros de seguridad
// legible por el portal en las specs revisadas para SPEC-201. Este componente
// recibe el valor como prop con un default conservador documentado, y expone
// el punto de enganche para cuando ese endpoint exista. No es una decisión de
// negocio tomada acá: es un hueco de contrato para api-http.
const WARNING_MINUTES_BEFORE = 2;
const RETURN_PATH_KEY = "starcircle.portal.returnPath";

const ACTIVITY_EVENTS: Array<keyof DocumentEventMap> = [
  "mousedown",
  "keydown",
  "touchstart",
  "wheel",
];

export function IdleSessionGuard({
  children,
  idleMinutes,
}: {
  children: ReactNode;
  idleMinutes: number;
}) {
  const { session, signOut, applyRenewal } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [showWarning, setShowWarning] = useState(false);
  const lastActivity = useRef(Date.now());
  if (!Number.isFinite(idleMinutes) || idleMinutes <= 0) {
    throw new Error("IdleSessionGuard requires a positive idleMinutes value");
  }
  const warnAt = Math.max(idleMinutes - WARNING_MINUTES_BEFORE, 0) * 60_000;
  const idleExpireAt = idleMinutes * 60_000;

  const resetActivity = useCallback(() => {
    lastActivity.current = Date.now();
    setShowWarning(false);
  }, []);

  const extendSession = useCallback(async () => {
    try {
      const renewal = await renewSession();
      applyRenewal(renewal);
      resetActivity();
    } catch {
      saveReturnPath(location.pathname + location.search);
      await signOut();
      navigate("/access", { replace: true });
    }
  }, [applyRenewal, location, navigate, resetActivity, signOut]);

  useEffect(() => {
    if (!session) return;
    for (const event of ACTIVITY_EVENTS) {
      document.addEventListener(event, resetActivity);
    }
    return () => {
      for (const event of ACTIVITY_EVENTS) {
        document.removeEventListener(event, resetActivity);
      }
    };
  }, [session, resetActivity]);

  useEffect(() => {
    if (!session) return;
    const interval = setInterval(() => {
      const elapsed = Date.now() - lastActivity.current;
      const absoluteRemaining = Date.parse(session.expiresAt) - Date.now();
      if (elapsed >= idleExpireAt || absoluteRemaining <= 0) {
        saveReturnPath(location.pathname + location.search);
        void signOut().finally(() => navigate("/access", { replace: true }));
      } else if (elapsed >= warnAt) {
        setShowWarning(true);
      }
    }, 5_000);
    return () => {
      clearInterval(interval);
    };
  }, [session, warnAt, idleExpireAt, signOut, navigate, location]);

  return (
    <>
      {children}
      {showWarning ? (
        <div
          role="alertdialog"
          style={{
            position: "fixed",
            bottom: 24,
            right: 24,
            padding: "16px 20px",
            borderRadius: tokens.radius.sm,
            background: tokens.surface.bg2,
            border: `1px solid ${tokens.surface.line2}`,
            boxShadow: tokens.shadow,
            maxWidth: 320,
            zIndex: 1000,
          }}
        >
          <div
            style={{
              font: `600 13px ${tokens.font.ui}`,
              color: tokens.text.primary,
              marginBottom: 6,
            }}
          >
            Tu sesión está por expirar
          </div>
          <div
            style={{
              font: `400 12.5px ${tokens.font.ui}`,
              color: tokens.text.secondary,
              marginBottom: 12,
            }}
          >
            Por inactividad, la sesión se cerrará en menos de{" "}
            {String(WARNING_MINUTES_BEFORE)} minutos.
          </div>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => void extendSession()}
          >
            Continuar sesión
          </button>
        </div>
      ) : null}
    </>
  );
}

// Consumida por PortalShell al montar con una sesión vigente (spec-verifier,
// criterio 5 de §6): navega a la ruta guardada antes de la expiración, una
// única vez, y la borra.
export function consumeReturnPath(): string | null {
  const path = sessionStorage.getItem(RETURN_PATH_KEY);
  if (path) sessionStorage.removeItem(RETURN_PATH_KEY);
  return isSafeReturnPath(path) ? path : null;
}

// Misma clave que usa el cierre por inactividad de arriba, para que
// PortalShell pueda retomar la ruta pedida (no solo la de una expiración)
// cuando todavía no hay sesión — ver comentario en PortalShell.tsx.
export function saveReturnPath(path: string): void {
  if (isSafeReturnPath(path)) sessionStorage.setItem(RETURN_PATH_KEY, path);
}

function isSafeReturnPath(path: string | null): path is string {
  return Boolean(path && path.startsWith("/") && !path.startsWith("//"));
}
